"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.commentsContract = exports.commentScoreResultSchema = exports.commentStatsSchema = exports.myTopLevelCommentSchema = exports.commentWithAncestorsSchema = exports.commentTreeNodeSchema = exports.commentResultSchema = exports.rejectionResultSchema = exports.commentSchema = exports.moderationStateSchema = exports.commentTextSchema = exports.COMMENT_TEXT_MAX_LENGTH = exports.commentPropertyKeys = exports.errorInvalidCursor = exports.boundedJsonSchema = void 0;
exports.errorPerspectiveViolationNotConfirmable = errorPerspectiveViolationNotConfirmable;
exports.errorNotMinorViolation = errorNotMinorViolation;
exports.errorPinLimitReached = errorPinLimitReached;
/**
 * Comments resource contract — schemas, routes, and typed errors for reading
 * (`list`, `myTopLevel`, `stats`, `reactionsMine`, `upvotesMine`) and writing
 * (`create` / `update` / `remove` / `confirm` / `pin` / `unpin`, plus the
 * reaction/upvote setters).
 *
 * Read and write share one comment shape: the normalized `commentSchema`
 * (`commentKey` / `authorKey` / `createdAt`, feature values in a `properties`
 * bag, `moderationState` for moderation state). `list` returns a ranked,
 * paginated tree of `{ comment, boosted, replies }` nodes (recursive
 * `replies`); all ranking, filtering, and boosting happen server-side (see
 * `server/modules/comments/comment-view.ts`). This is the canonical `Comment`
 * clients render; the server maps its stored `StoredComment` rows to it.
 *
 * `stats` returns instance-wide aggregates computed server-side by the
 * server's comment-stats helper — aggregate-only; callers that need
 * specific comment objects use `list`, not this. Its route is
 * `/comments-stats` (a
 * `[resource]-[sub-thing]` sibling of `/comments`, not `/comments/stats`) so a
 * future `/comments/{commentKey}` single-comment GET isn't shadowed.
 * `commentCount` and `topLevelCount` are always present; everything else a
 * feature contributes goes into one of two always-present dictionaries.
 * `counts` holds scalar per-feature totals (e.g. `counts.entryPoints`) and
 * `breakdowns` holds bucketed `bucket -> count` maps (e.g. `breakdowns.slider`,
 * `breakdowns.perspectives`). Both are empty when no feature contributes, so
 * callers guard only `stats?.` (the loading state), not the dictionaries.
 *
 * The teaser-embed payload and the featured-comment mutations live on the
 * *spaces* contract (`spaces.teaser` / `spaces.featureComment` /
 * `spaces.unfeatureComment`), not here: they model space-level state (a space
 * has at most one featured comment, previewed by its teaser), and only read
 * through this resource's comment shape. `commentSchema.featuredInTeaser`
 * is the read-time flag those mutations toggle.
 *
 * Write: `create` / `update` return a discriminated result, not a thrown
 * error. A `posted` result carries the stored comment (live, or hidden for
 * human review — see `moderationState.inReview`). A `rejected` result carries
 * no comment; the server keeps a small rejection record under the same key so
 * the author can `confirm` it (post-anyway for a minor violation, or with an
 * appeal note for a major one). When moderation can't run, the result is
 * `rejected` with `systemDown: true`. A rejection is an expected outcome that
 * records real state, so it is a return value rather than an error.
 *
 * Manual moderation is *not* a rejection: those comments are written with
 * `moderationState.inReview: true` and queued for a human moderator.
 * Consumers should hide in-review comments from anyone other than the author
 * and admins.
 *
 * `myTopLevel` returns the viewer's own top-level comment and the count of
 * its visible replies — text-less slider votes included, no ranking.
 *
 * `score` runs LLM ranking-scoring for a comment after `create`/`update`, when
 * the space has `llm-ranking` enabled — idempotent per comment version, with an
 * admin-only `force` to re-score.
 */
const client_1 = require("@orpc/client");
const z = __importStar(require("zod"));
const community_guidelines_1 = require("../../community-guidelines");
const locale_1 = require("../../locale");
const perspectives_1 = require("../../perspectives");
const roles_capabilities_1 = require("../../roles-capabilities");
const api_schemas_1 = require("../api-schemas");
const base_1 = require("../base");
const multi_language_1 = require("../multi-language");
const schemas_1 = require("../schemas");
const types_1 = require("../types");
var api_schemas_2 = require("../api-schemas");
Object.defineProperty(exports, "boundedJsonSchema", { enumerable: true, get: function () { return api_schemas_2.boundedJsonSchema; } });
Object.defineProperty(exports, "errorInvalidCursor", { enumerable: true, get: function () { return api_schemas_2.errorInvalidCursor; } });
/**
 * Allowlist of keys a client may set in `comment.properties`. The write API
 * rejects unknown keys with `BAD_REQUEST`, and the wire shape only carries
 * these. Add a key here (grouped by the owning feature) to register a new
 * comment property.
 */
exports.commentPropertyKeys = new Set([
    // Core
    'about',
    'entryUrl',
    'slider',
]);
/**
 * Bounds on a comment's `text` length.
 */
exports.COMMENT_TEXT_MAX_LENGTH = 20000;
exports.commentTextSchema = z.string().max(exports.COMMENT_TEXT_MAX_LENGTH);
/**
 * A comment's moderation status, present only once it's been through
 * moderation review (flagged for review, judged, or both) — absent means the
 * comment is live and was never flagged. Hidden client-side from
 * non-author, non-admin viewers.
 */
exports.moderationStateSchema = z.object({
    inReview: z.boolean().describe('True while the comment is held for human moderation (manual or AI+manual).'),
    rejected: z
        .boolean()
        .describe('True when a moderator rejected this comment. Computed from the stored violates/humanJudgement fields, not stored itself.'),
    violations: z
        .array(community_guidelines_1.moderationRuleSchema)
        .optional()
        .describe('Stable rule-violation keys identified for this comment (translated client-side via a t() map; the keys stay stable across wording changes to the rules). Present whenever inReview or rejected is true, absent otherwise.'),
});
// Output-only (never part of any `.input(...)`) — see the length-limits note
// in docs/architecture/orpc.md for why only inputs are bounded.
exports.commentSchema = z
    .object({
    commentKey: z.string().describe("The comment's unique key within its space."),
    spaceKey: z.string().describe('The space this comment belongs to.'),
    parentKey: z.string().nullable().describe('The comment this one replies to, or null for a top-level comment.'),
    authorKey: z.string().describe("The comment author's persona key."),
    text: multi_language_1.multiLanguageStringSchema.describe('The comment body. Blank when the comment has been deleted.'),
    originalLanguage: z
        .enum(locale_1.ALL_ISO_LOCALES)
        .optional()
        .describe('The language the comment was written in, when known — independent of whether `text` was translated.'),
    properties: z.record(z.string(), api_schemas_1.boundedJsonSchema).describe('Feature-specific values attached to the comment (e.g. `slider`).'),
    perspectives: z
        .array(perspectives_1.commentPerspectiveTagSchema)
        .optional()
        .describe('Perspective tags on the comment: a preset carries the label and emoji the space was configured with when it was ' +
        'posted, a write-in carries only its text. Absent when the comment has none.'),
    createdAt: schemas_1.isoDateTime.describe('Creation time as an RFC 3339 UTC timestamp.'),
    editedAt: schemas_1.isoDateTime.nullable().describe('Last-edit time as an RFC 3339 UTC timestamp, or null if never edited.'),
    deleted: z.boolean().describe('Whether the comment has been soft-deleted; the row is kept but its text is blanked.'),
    moderationState: exports.moderationStateSchema
        .optional()
        .describe("The comment's moderation status, present only once it has been through review; absent means live and never flagged."),
    pinned: z
        .boolean()
        .describe("Whether this comment is pinned to the top of the feed (all top-level comments, or this comment's reply-siblings, are ranked independently). Computed at read time from the pin-to-feed instance properties, not stored."),
    featuredInTeaser: z
        .boolean()
        .describe('Whether this comment is featured for the external teaser preview. Computed at read time from the teaser-feature instance property.'),
    author: types_1.userProfileSchema
        .nullable()
        .describe('Profile of the comment author, or null if the author has no persona record (e.g. a deleted user).'),
    reactionCounts: z.record(z.string(), z.number()).describe('Per-reaction-type tally of reactions on this comment.'),
    upvoteCount: z.number().describe('Total upvotes on this comment.'),
})
    .describe('A single comment in a space, with its author, moderation state, and reaction tallies resolved for display.');
// Output-only.
exports.rejectionResultSchema = z
    .object({
    rejectionKey: z
        .string()
        .describe('The key the comment would have had; identifies the stored rejection record for a later confirm.'),
    violations: z.array(community_guidelines_1.moderationRuleSchema).describe('Machine-readable codes for the moderation rules the text tripped.'),
    minorViolation: z
        .boolean()
        .describe('Whether every violation is non-severe, letting the author post the comment anyway without an appeal.'),
    systemDown: z
        .boolean()
        .describe("True when moderation couldn't run (e.g. the AI was unavailable); the author can still confirm with an appeal. Never a minorViolation."),
    perspectiveViolations: z
        .array(community_guidelines_1.moderationRuleSchema)
        .optional()
        .describe("Machine-readable codes for the moderation rules the comment's write-in perspective tripped, rather than its body. " +
        'A rejection carrying any of these cannot be confirmed: there is no moderator review for a perspective, so the ' +
        'author has to change it. The Representing Perspectives feature renders these beside the field they came from.'),
})
    .describe('Why a create/update was rejected by moderation, with the tripped guidelines and whether the author can still post.');
// Output-only.
exports.commentResultSchema = z
    .discriminatedUnion('outcome', [
    z.object({ outcome: z.literal('posted').describe('The comment was stored (live or held for review).'), comment: exports.commentSchema }),
    z.object({ outcome: z.literal('rejected').describe('The comment was rejected and not stored.'), rejection: exports.rejectionResultSchema }),
])
    .describe('Outcome of a create/update: `posted` carries the stored comment (live or held for review); `rejected` carries only a rejection record and stores no comment.');
// Output-only.
exports.commentTreeNodeSchema = z
    .object({
    comment: exports.commentSchema,
    boosted: z.boolean().describe('Whether ranking surfaced (boosted) this comment above its default position.'),
    get replies() {
        return z.array(exports.commentTreeNodeSchema).describe('Nested replies to this comment, ranked and depth-limited like the top level.');
    },
})
    .describe('A comment plus its ranked reply subtree, as returned by the ranked feed.');
exports.commentWithAncestorsSchema = z.object({
    comment: exports.commentSchema,
    ancestors: z.array(exports.commentSchema),
    replies: z.array(exports.commentTreeNodeSchema),
    nextCursor: z.string().nullable(),
});
const depthParam = { depth: z.number().int().min(0).optional() };
const list = base_1.baseProcedure
    .route({
    method: 'GET',
    path: '/silo/{siloKey}/spaces/{spaceKey}/comments',
    tags: ['Comments'],
    summary: 'List ranked comments with their replies',
    description: "List the space's comment feed. Returns a page of top-level comments in ranked order, each carrying its replies nested beneath it. " +
        '`sort` picks the ranker, and `depth` caps how far replies nest, with 0 returning top-level comments alone. ' +
        'Pinned comments come first regardless of the ranker.',
})
    .meta({ access: 'public' })
    .input(z.object({ ...api_schemas_1.spaceReadParams, ...api_schemas_1.cursorParams, ...depthParam, sort: api_schemas_1.shortString }))
    .output(z.object({
    items: z.array(exports.commentTreeNodeSchema),
    nextCursor: z.string().nullable(),
}))
    .errors(api_schemas_1.invalidCursorErrors);
// `sort` is optional here (unlike `list`): it only orders replies, which are
// returned only when `depth` > 0. The jump-to-comment caller omits it.
const get = base_1.baseProcedure
    .route({
    method: 'GET',
    path: '/silo/{siloKey}/spaces/{spaceKey}/comments/{commentKey}',
    tags: ['Comments'],
    summary: 'Get one comment with its parents and replies',
    description: 'Get one comment together with the chain of ancestors above it, root first and immediate parent last, plus a page of its replies. ' +
        'This returns the comment within its own context. ' +
        '`depth` caps how far replies nest, and `sort` orders replies.',
})
    .meta({ access: 'public' })
    .input(z.object({ ...api_schemas_1.spaceReadParams, commentKey: api_schemas_1.shortString, ...api_schemas_1.cursorParams, ...depthParam, sort: api_schemas_1.shortString.optional() }))
    .output(exports.commentWithAncestorsSchema)
    .errors(api_schemas_1.invalidCursorErrors);
exports.myTopLevelCommentSchema = z.object({
    comment: exports.commentSchema.nullable(),
    replyCount: z.number(),
});
const myTopLevel = base_1.baseProcedure
    .route({
    method: 'GET',
    path: '/silo/{siloKey}/spaces/{spaceKey}/comments-my-top-level',
    tags: ['Comments'],
    summary: 'Get your own top-level comment in the space',
    description: 'Get your own top-level comment in this space, with the number of visible replies to it. ' +
        'Most formats allow one top-level comment per person.',
})
    .meta({ access: 'public' })
    .input(z.object(api_schemas_1.spaceReadParams))
    .output(exports.myTopLevelCommentSchema);
exports.commentStatsSchema = z
    .object({
    commentCount: z.number().describe('Total visible comments in the space, including replies.'),
    topLevelCount: z.number().describe('Number of visible top-level (non-reply) comments.'),
    counts: z
        .record(z.string(), z.number())
        .describe('Scalar per-feature totals keyed by feature (e.g. `entryPoints`). Empty when no feature contributes.'),
    breakdowns: z
        .record(z.string(), z.record(z.string(), z.number()))
        .describe('Bucketed `bucket -> count` maps keyed by feature (e.g. `slider`, `perspectives`). Empty when no feature contributes.'),
})
    .describe('Aggregate comment counts for a space, including per-feature scalar totals and bucketed breakdowns.');
const stats = base_1.baseProcedure
    .route({
    method: 'GET',
    path: '/silo/{siloKey}/spaces/{spaceKey}/comments-stats',
    tags: ['Comments'],
    summary: 'Get comment stats for a space',
    description: 'Get the aggregate counts a space displays around its feed: how many visible comments in total, how many of those are ' +
        'top-level, plus per-feature totals and bucketed breakdowns. ' +
        "The breakdowns are where a format's tallies live: `slider` positions, `perspectives` tags, and the `poll` results on an Audience Input space. " +
        'A feature that is switched off contributes an empty map, which means the feature is off rather than that nobody has answered.',
})
    .meta({ access: 'public' })
    .input(z.object(api_schemas_1.spaceReadParams))
    .output(exports.commentStatsSchema);
const create = base_1.baseProcedure
    .route({
    method: 'POST',
    path: '/silo/{siloKey}/spaces/{spaceKey}/comments',
    tags: ['Comments'],
    summary: 'Post a comment to a space',
    description: 'Post a comment to a space, or a reply if you send `parentKey`, and get it back with the server-minted `commentKey`. ' +
        'Moderation runs on the text before anything is stored, and the result says which way it went: `posted`, meaning live or ' +
        'hidden pending human review (see `comment.moderationState.inReview`), or `rejected`, meaning only a rejection record was ' +
        'stored under the minted key, which the author can `confirm`. ' +
        'When moderation itself is unavailable the result is `rejected` with `systemDown: true`, a temporary failure rather than a judgement on the text. ' +
        'A write-in perspective in `perspectives` is moderated the same way, and a rejected one rejects the whole post, with its ' +
        'guidelines under `rejection.perspectiveViolations`; when its check is the one that could not run, the result is `systemDown: true` ' +
        'with no perspective guidelines.',
})
    .meta({ access: 'public' })
    .input(z.object({
    ...api_schemas_1.spaceParams,
    text: exports.commentTextSchema,
    parentKey: api_schemas_1.shortString.nullable().optional(),
    properties: (0, api_schemas_1.boundedJsonRecord)().optional(),
    perspectives: z
        .array(perspectives_1.commentPerspectiveInputSchema)
        .max(perspectives_1.MAX_COMMENT_PERSPECTIVES)
        .optional()
        .describe('Perspective tags to set: a preset is a bare key the server resolves against the space list, a write-in is ' +
        "free text the server moderates. Omit to leave the comment's perspectives alone; send [] to clear them."),
}))
    .output(exports.commentResultSchema)
    .errors({});
const update = base_1.baseProcedure
    .route({
    method: 'PATCH',
    path: '/silo/{siloKey}/spaces/{spaceKey}/comments/{commentKey}',
    tags: ['Comments'],
    summary: 'Edit a comment',
    description: 'Edit your own comment, replacing its text or its properties. ' +
        'The edit is moderated and returns the same result shape as `comments.create`. A write-in perspective is checked even when the ' +
        'edit leaves the text alone, and is not re-checked when it matches the one the comment already carries.',
})
    .meta({ access: 'public' })
    .input(z.object({
    ...api_schemas_1.spaceParams,
    commentKey: api_schemas_1.shortString,
    text: exports.commentTextSchema.optional(),
    properties: (0, api_schemas_1.boundedJsonRecord)().optional(),
    perspectives: z
        .array(perspectives_1.commentPerspectiveInputSchema)
        .max(perspectives_1.MAX_COMMENT_PERSPECTIVES)
        .optional()
        .describe('Perspective tags to set: a preset is a bare key the server resolves against the space list, a write-in is ' +
        "free text the server moderates. Omit to leave the comment's perspectives alone; send [] to clear them."),
}))
    .output(exports.commentResultSchema)
    .errors({});
const remove = base_1.baseProcedure
    .route({
    method: 'DELETE',
    path: '/silo/{siloKey}/spaces/{spaceKey}/comments/{commentKey}',
    tags: ['Comments'],
    summary: 'Soft-delete a comment',
    description: 'Delete your own comment. The row is kept but blanked and marked deleted, preserving the comment tree.',
})
    .meta({ access: 'public' })
    .input(z.object({
    ...api_schemas_1.spaceParams,
    commentKey: api_schemas_1.shortString,
}))
    .output(z.void())
    .errors({});
const confirm = base_1.baseProcedure
    .route({
    method: 'POST',
    path: '/silo/{siloKey}/spaces/{spaceKey}/comments/{commentKey}/confirm',
    tags: ['Comments'],
    summary: 'Confirm a comment after a rejection',
    description: 'Post a comment that moderation rejected, once its author has confirmed they meant it, and return the stored comment. ' +
        'It may still be held for review rather than appearing at once. ' +
        'A rejection for a major violation cannot be confirmed on its own: send `appealText`, or the call is refused with NOT_MINOR_VIOLATION. ' +
        'A rejection carrying `perspectiveViolations` cannot be confirmed at all, by anyone: there is no moderator review for a perspective, so PERSPECTIVE_VIOLATION_NOT_CONFIRMABLE is returned and the author has to change it.',
})
    .meta({ access: 'public' })
    .input(z.object({
    ...api_schemas_1.spaceParams,
    commentKey: api_schemas_1.shortString,
    appealText: api_schemas_1.mediumText.optional(),
}))
    .output(exports.commentSchema)
    .errors({
    NOT_MINOR_VIOLATION: { status: 400, data: z.object({ commentKey: z.string() }) },
    PERSPECTIVE_VIOLATION_NOT_CONFIRMABLE: { status: 400, data: z.object({ commentKey: z.string() }) },
});
const setReaction = base_1.baseProcedure
    .route({
    method: 'PUT',
    path: '/silo/{siloKey}/spaces/{spaceKey}/comments/{commentKey}/reaction',
    tags: ['Comments'],
    summary: 'Set your reaction on a comment',
    description: 'Set your reaction on a comment and return the updated comment. ' +
        'Each person can have one reaction per comment. ' +
        'A different type replaces the existing reaction, while the same type has no effect.',
})
    .meta({ access: 'public' })
    .input(z.object({ ...api_schemas_1.spaceParams, commentKey: api_schemas_1.shortString, reactionType: api_schemas_1.shortString }))
    .output(exports.commentSchema)
    .errors({});
const removeReaction = base_1.baseProcedure
    .route({
    method: 'DELETE',
    path: '/silo/{siloKey}/spaces/{spaceKey}/comments/{commentKey}/reaction',
    tags: ['Comments'],
    summary: 'Remove your reaction from a comment',
    description: 'Remove your reaction from a comment and return the updated comment. Safe to call even if you have not reacted.',
})
    .meta({ access: 'public' })
    .input(z.object({ ...api_schemas_1.spaceParams, commentKey: api_schemas_1.shortString }))
    .output(exports.commentSchema)
    .errors({});
const setUpvote = base_1.baseProcedure
    .route({
    method: 'PUT',
    path: '/silo/{siloKey}/spaces/{spaceKey}/comments/{commentKey}/upvote',
    tags: ['Comments'],
    summary: 'Upvote a comment',
    description: 'Upvote a comment and return the updated comment. ' +
        'Safe to call if you have already upvoted it. ' +
        'On Audience Input spaces, upvotes determine response ranking.',
})
    .meta({ access: 'public' })
    .input(z.object({ ...api_schemas_1.spaceParams, commentKey: api_schemas_1.shortString }))
    .output(exports.commentSchema)
    .errors({});
const removeUpvote = base_1.baseProcedure
    .route({
    method: 'DELETE',
    path: '/silo/{siloKey}/spaces/{spaceKey}/comments/{commentKey}/upvote',
    tags: ['Comments'],
    summary: 'Remove your upvote from a comment',
    description: 'Remove your upvote from a comment and return the updated comment. Safe to call even if you have not upvoted it.',
})
    .meta({ access: 'public' })
    .input(z.object({ ...api_schemas_1.spaceParams, commentKey: api_schemas_1.shortString }))
    .output(exports.commentSchema)
    .errors({});
// The viewer's own reaction/upvote state is per-viewer and so is kept off the
// shared comment (`commentSchema` carries only the reaction/upvote
// *tallies*, identical for every viewer). These two `authenticated` reads
// return the caller's state for the whole space in one request each, keyed by
// comment, mirroring `poll-votes-mine`. Clients join them to the tree by
// `commentKey`.
const reactionsMine = base_1.baseProcedure
    .route({
    method: 'GET',
    path: '/silo/{siloKey}/spaces/{spaceKey}/reactions-mine',
    tags: ['Comments'],
    summary: 'Get your reactions in the space',
    description: 'Get your own reaction on each comment in this space, as a map of comment key to reaction type. ' +
        'A comment you have not reacted to is absent. ' +
        'Only your own state is here: the complete count is included with the comments.',
})
    .meta({ access: 'authenticated' })
    .input(z.object(api_schemas_1.spaceReadParams))
    .output(z.record(z.string(), z.string()));
const upvotesMine = base_1.baseProcedure
    .route({
    method: 'GET',
    path: '/silo/{siloKey}/spaces/{spaceKey}/upvotes-mine',
    tags: ['Comments'],
    summary: 'List your upvotes in the space',
    description: 'List the comments you have upvoted in this space, as an array of comment keys.',
})
    .meta({ access: 'authenticated' })
    .input(z.object(api_schemas_1.spaceReadParams))
    .output(z.array(z.string()));
const pin = base_1.baseProcedure
    .route({
    method: 'PUT',
    path: '/silo/{siloKey}/spaces/{spaceKey}/comments/{commentKey}/pin',
    tags: ['Comments'],
    summary: 'Pin or reposition a comment',
    description: 'Pin a comment above the rest of its group, or reposition one that is already pinned, and return the updated comment. ' +
        'A pinned comment is moved to the front whatever the ranker would otherwise do. ' +
        "`position` is the target index within the comment's own group, which is either all top-level comments or its reply-siblings. " +
        'Omit it to append after the current last pinned one. ' +
        'A group holds 5 pinned top-level comments, or 2 per reply parent, and pinning into a full group is refused with PIN_LIMIT_REACHED.',
})
    // Shared steward-comments capability; open to every admin role (#5270).
    .meta({ access: 'admin', capability: roles_capabilities_1.STEWARD_COMMENTS_CAPABILITY })
    .input(z.object({ ...api_schemas_1.spaceParams, commentKey: api_schemas_1.shortString, position: z.number().int().min(0).optional() }))
    .output(exports.commentSchema)
    .errors({
    PIN_LIMIT_REACHED: { status: 400, data: z.object({ commentKey: z.string(), limit: z.number() }) },
});
const unpin = base_1.baseProcedure
    .route({
    method: 'DELETE',
    path: '/silo/{siloKey}/spaces/{spaceKey}/comments/{commentKey}/pin',
    tags: ['Comments'],
    summary: 'Unpin a comment from the feed',
    description: 'Unpin a comment and return the updated comment. ' +
        'Only the pinned placement goes: the comment stays in the feed wherever the ranker puts it.',
})
    // Shared steward-comments capability; open to every admin role (#5270).
    .meta({ access: 'admin', capability: roles_capabilities_1.STEWARD_COMMENTS_CAPABILITY })
    .input(z.object({ ...api_schemas_1.spaceParams, commentKey: api_schemas_1.shortString }))
    .output(exports.commentSchema)
    .errors({});
exports.commentScoreResultSchema = z.object({
    scored: z
        .boolean()
        .describe('True when the comment is scored for its current version, whether this call ran scoring or it was already scored; false only when the space lacks the `llm-ranking` feature.'),
});
const score = base_1.baseProcedure
    .route({
    method: 'POST',
    path: '/silo/{siloKey}/spaces/{spaceKey}/comments/{commentKey}/score',
    tags: ['Comments'],
    summary: 'Score a comment for ranking',
    description: 'Score a comment for LLM ranking, which a client calls after posting or editing one so the new text can be ranked. ' +
        'On a space without the `llm-ranking` feature this reports `scored: false`. ' +
        'Otherwise it is idempotent per version of the comment: a version already scored is not scored again. ' +
        '`force` re-scores regardless and needs admin access, else FORBIDDEN.',
})
    .meta({ access: 'public' })
    .input(z.object({ ...api_schemas_1.spaceParams, commentKey: api_schemas_1.shortString, force: z.boolean().optional() }))
    .output(exports.commentScoreResultSchema)
    .errors({});
/**
 * The rejection was for the comment's perspective, not its body, so it cannot
 * be confirmed: there is no moderator review for a perspective. The author
 * changes the perspective and posts again.
 */
function errorPerspectiveViolationNotConfirmable(commentKey) {
    return new client_1.ORPCError('PERSPECTIVE_VIOLATION_NOT_CONFIRMABLE', {
        status: 400,
        data: { commentKey },
        message: `Comment ${commentKey} was rejected for its perspective and cannot be confirmed`,
    });
}
function errorNotMinorViolation(commentKey) {
    return new client_1.ORPCError('NOT_MINOR_VIOLATION', {
        status: 400,
        data: { commentKey },
        message: `Comment ${commentKey} is not flagged as a minor violation`,
    });
}
function errorPinLimitReached(commentKey, limit) {
    return new client_1.ORPCError('PIN_LIMIT_REACHED', {
        status: 400,
        data: { commentKey, limit },
        message: `Comment ${commentKey}'s pin group is already at its limit of ${limit}`,
    });
}
const report = base_1.baseProcedure
    .route({
    method: 'POST',
    path: '/silo/{siloKey}/spaces/{spaceKey}/comments/{commentKey}/report',
    tags: ['Comments'],
    summary: 'Report a comment',
    description: 'Report a comment for a moderator to look at, naming the rule keys you think it breaks (or `OTHER`). ' +
        'This puts the comment into the moderation queue with the report attached; a second report on the same comment adds its broken rules to the pending task. ' +
        'You cannot report your own comment, which is refused with FORBIDDEN. ' +
        'Has no return value.',
})
    .meta({ access: 'authenticated' })
    .input(z.object({
    ...api_schemas_1.spaceParams,
    commentKey: api_schemas_1.shortString,
    violations: z
        .array(community_guidelines_1.reportReasonSchema)
        .max(api_schemas_1.MAX_ARRAY_LENGTH)
        .describe("Stable keys for what the reporter says is wrong: community guidelines, or `OTHER` for an objection that isn't one."),
}))
    .errors({});
exports.commentsContract = {
    list,
    get,
    myTopLevel,
    stats,
    create,
    update,
    remove,
    confirm,
    setReaction,
    removeReaction,
    setUpvote,
    removeUpvote,
    reactionsMine,
    upvotesMine,
    pin,
    unpin,
    report,
    score,
};
//# sourceMappingURL=comments.js.map